import React, { useContext, useEffect, useState } from 'react';

import FeedItemList from './FeedItemList';
import FeedsTabs from './FeedsTabs';
import PostcodePrompt from './PostcodePrompt';

import { trackGAEvent, trackGAEventOnce } from '../../../../utils/gaTracking';
import getFeedItems from '../../repositories/getFeedItems';
import saveActiveFeed from '../../repositories/saveActiveFeed';
import MpCardBanner from '../Marketing/MpCardBanner';
import convertSnakeToPascalCase from './utils/convertSnakeToPascalCase';

import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import LoginDialog from '../../components/LoginDialog/LoginDialog';
import MotorsTourModal from '../MotorsTour/MotorsTourModal';
import getMpCards from '../../repositories/getMpCards';
import { TFeed, TFeedItem, TFeeds } from 'homepage-web-bff/types/TFeeds';
import { TMpCard } from 'homepage-web-bff/types/TMpCard';
import isCarItem from './utils/isCarItem';
import FEED_ITEMS_PER_PAGE from '../../constants/feedItemsPerPage';

type TFeedsProps = {
  feeds: TFeeds;
  feedPostcode?: string;
  initialFeed?: number;
  favoriteAdIds?: string[];
};

/**
 * Postcode prompt is shown if feed requires location but user postcode is not known
 */
const shouldShowPostcodePrompt = (feed: TFeed, feedPostcode?: string): boolean =>
  feed?.locationRequired && !feedPostcode;

const SEEN_LOGIN_GATE = 'SeenLoginGate';

const wasLoginDialogShown = () => typeof window !== 'undefined' && localStorage.getItem(SEEN_LOGIN_GATE) === 'true';

const SEEN_MOTORS_TOUR_KEY = 'SeenMotorsTour';
const MIN_CAR_ITEMS_FOR_MOTORS_TOUR = 4;

const wasMotorsTourSeen = () => typeof window !== 'undefined' && localStorage.getItem(SEEN_MOTORS_TOUR_KEY) === 'true';

const Feeds = ({ feeds, feedPostcode, initialFeed = 0, favoriteAdIds }: TFeedsProps) => {
  const [activeFeedIndex, setActiveFeedIndex] = useState(initialFeed);
  const [feedsPages, setFeedsPages] = useState({});
  const [items, setItems] = useState<TFeedItem[]>([]);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showLoginDialog, setShowLoginDialog] = useState<boolean>(false);
  const [mpCards, setMpCards] = useState<TMpCard[]>([]);
  const [isMpCardsLoading, setIsMpCardsLoading] = useState<boolean>(true);
  const {
    userLoggedIn,
    isBot,
    hasConsentBannerBeenDismissed,
    experiments,
    showSmartBanner,
    tenantContext: { tenant },
    xsrfToken,
  } = useContext(EnvironmentContext);

  const { isMpCardSingleTileEnabled, isMpCardDoubleTileEnabled, isMpCardBannerEnabled, isMotorsTourEnabled } =
    experiments;
  const isMp = (tenant ?? '') === 'mp';
  const [showMotorsTour, setShowMotorsTour] = useState(false);
  const [hasSeenMotorsTour, setHasSeenMotorsTour] = useState(wasMotorsTourSeen);

  useEffect(() => {
    const fetchMarketingCards = async () => {
      if (isMp && (isMpCardSingleTileEnabled || isMpCardDoubleTileEnabled || isMpCardBannerEnabled)) {
        setIsMpCardsLoading(true);
        try {
          const cards = await getMpCards();
          setMpCards(cards);
        } finally {
          setIsMpCardsLoading(false);
        }
      }
    };
    void fetchMarketingCards();
  }, [isMpCardSingleTileEnabled, isMpCardDoubleTileEnabled, isMpCardBannerEnabled, isMp]);

  const fetchFeedItems = async (feed: TFeed, page: number): Promise<void> => {
    setIsLoading(true);
    const feedItems = await getFeedItems({ feed, feedPostcode, page, favoriteAdIds }, userLoggedIn, xsrfToken);
    if (feedItems?.length) {
      setItems((prevItems) => [...prevItems, ...feedItems]);
      setHasNextPage(true);
    } else {
      // having no items back means feed-api is either broken or has no data for feed
      // anyway infinite scroll should be disabled in this case
      setHasNextPage(false);
    }

    if (page === 0) {
      const eventLabel = convertSnakeToPascalCase(feed.feedType);
      if (feedItems.length) {
        trackGAEvent('Displayed', eventLabel);
        trackGAEventOnce('FirstLoadNotEmpty', eventLabel);
      } else {
        trackGAEvent('EmptyStateDisplayed', eventLabel);
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    if (!feeds.length) {
      return;
    }

    const pages =
      feeds.length &&
      feeds.reduce(
        (acc, feed) => ({
          ...acc,
          [feed.feedType]: 0,
        }),
        {},
      );
    const feed = feeds[activeFeedIndex];

    setFeedsPages(pages);
    setActiveFeedIndex(activeFeedIndex);
    saveActiveFeed(activeFeedIndex);

    const eventLabel = convertSnakeToPascalCase(feed.feedType);
    trackGAEvent('TabSelected', eventLabel);

    const page = pages[feed.feedType];
    if (page === 0) {
      trackGAEvent(`LoadMore${page}`, eventLabel);
    }

    // try to restore state from cache or fetch from server
    void fetchFeedItems(feed, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isBot && !userLoggedIn && hasConsentBannerBeenDismissed && !wasLoginDialogShown()) {
      setShowLoginDialog(true);
      localStorage.setItem(SEEN_LOGIN_GATE, 'true');
    }
  }, [userLoggedIn, isBot, hasConsentBannerBeenDismissed]);

  const firstBatch = items.slice(0, FEED_ITEMS_PER_PAGE);
  const carItemCount = firstBatch.filter(isCarItem).length;

  // TEMPORAL: Show the motors product tour once:
  // - MP only
  // - feature flag enabled
  // - user should be logged-in or user has already seen a log-in dialog
  // - shouldn't compete with login dialog and app banner
  // - user hasn't seen the tour yet
  // - minimum 4 auto's in the first 40 recommended ads.
  const hasCarsInterest = activeFeedIndex === 0 && carItemCount >= MIN_CAR_ITEMS_FOR_MOTORS_TOUR;
  const canShowMotorsTour = userLoggedIn || wasLoginDialogShown();
  const canTriggerMotorsTour =
    isMp &&
    isMotorsTourEnabled &&
    canShowMotorsTour &&
    !showSmartBanner &&
    !showLoginDialog &&
    !hasSeenMotorsTour &&
    hasCarsInterest;

  useEffect(() => {
    if (canTriggerMotorsTour) {
      setShowMotorsTour(true);
    }
  }, [canTriggerMotorsTour]);

  const handleMotorsTourClose = () => {
    localStorage.setItem(SEEN_MOTORS_TOUR_KEY, 'true');

    setHasSeenMotorsTour(true);
    setShowMotorsTour(false);
  };

  const handleLoadMore = (pageIncrement = 1) => {
    const feed = feeds[activeFeedIndex];
    const page = feedsPages[feed.feedType] + pageIncrement;

    setFeedsPages((prevPages) => ({
      ...prevPages,
      [feed.feedType]: page,
    }));

    trackGAEvent(`LoadMore${page}`, convertSnakeToPascalCase(feed.feedType));
    // fetch new items
    void fetchFeedItems(feed, page);
  };

  const handleFeedChange = (index: number) => {
    setActiveFeedIndex(index);
    saveActiveFeed(index);
    // reset items when switching between tabs
    setItems([]);
    setHasNextPage(true);

    const feed = feeds[index];
    const page = feedsPages[feed.feedType];
    const eventLabel = convertSnakeToPascalCase(feed.feedType);
    trackGAEvent('TabSelected', eventLabel);

    if (!shouldShowPostcodePrompt(feeds[index], feedPostcode)) {
      if (page === 0) {
        trackGAEvent(`LoadMore${page}`, eventLabel);
      }
      // try to restore state from cache or fetch from server
      void fetchFeedItems(feed, page);
    }
  };

  if (!feeds.length) {
    return null;
  }

  return (
    <>
      {isMpCardBannerEnabled && !isMpCardsLoading && mpCards.length > 0 && <MpCardBanner mpCards={mpCards} />}
      <FeedsTabs feeds={feeds} activeFeedIndex={activeFeedIndex} onClick={handleFeedChange} />
      {shouldShowPostcodePrompt(feeds[activeFeedIndex], feedPostcode) ? (
        <PostcodePrompt />
      ) : (
        <FeedItemList
          activeFeedIndex={activeFeedIndex}
          items={items}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          onLoadMore={handleLoadMore}
          feedType={feeds[activeFeedIndex].feedType}
          mpCards={mpCards}
          setShowLoginDialog={setShowLoginDialog}
          {...(feeds[activeFeedIndex].locationRequired ? { feedPostCode: feedPostcode } : {})}
          isMp={isMp}
        />
      )}
      {showLoginDialog && <LoginDialog onClose={setShowLoginDialog} />}
      {showMotorsTour && <MotorsTourModal onClose={handleMotorsTourClose} />}
    </>
  );
};

export default Feeds;
